import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Alert,
  useWindowDimensions,
  Platform,
} from "react-native";
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { clearToastNotification } from '../../../store/slices/actionPlanSlice';
import { refetchNotifications } from '../../../store/slices/notificationSlice';
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import CrossPlatformDropdown from "../../../components/CrossPlatformDropdown";
import LhDatePicker from "../../../components/DatePicker/LhDatePicker";
import LhTextArea from "../../../components/LhTextArea";
import LhTextInput from "../../../components/LhTextInput";
import { CreateActionPlanFormProps } from '../types';
import { useCreateActionPlanForm } from '../hooks/useCreateActionPlanForm';
import { createActionPlanFormStyles } from '../styles/createActionPlanForm.styles';
import { useTheme } from "../../../theme";

const CreateActionPlanForm: React.FC<CreateActionPlanFormProps> = ({
  data = {},
  type = "create",
  onSubmit,
  onCancel,
  onFormSubmitReady,
  onViewAuditTrail,
  isMobile = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  // For date pickers on web, we need to check dark theme directly since web uses global CSS patcher
  const datePickerColorScheme = resolvedTheme === 'dark' ? 'dark' : 'light';
  const { width: screenWidth } = useWindowDimensions();
  const isTablet = screenWidth >= 768;

  const {
    title,
    setTitle,
    selectedSource,
    setSelectedSource,
    selectedType,
    setSelectedType,
    selectedPriority,
    setSelectedPriority,
    selectedOwner,
    selectedAssignee,
    setSelectedAssignee,
    startDate,
    setStartDate,
    targetDate,
    handleTargetDateChange,
    actionPlan,
    setActionPlan,
    notes,
    setNotes,
    frontlineInvolvement,
    setFrontlineInvolvement,
    selectedEmployee,
    setSelectedEmployee,
    isView,
    loading,
    dateValidationError,
    submissionError,
    validationErrors,
    setSubmissionError,
    assigneeLoading,
    employeeLoading,
    isCreateDisabled,
    sourceOptions,
    typeOptions,
    priorityOptions,
    ownerOption,
    assigneeList,
    employeeList,
    handleSubmit,
    formatDisplayDate,
  } = useCreateActionPlanForm(data, type, onSubmit);

  const dispatch = useDispatch<AppDispatch>();

  // Keep a ref to the latest handleSubmit so we can provide a stable callback to parent
  const handleSubmitRef = React.useRef(handleSubmit);
  React.useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // Expose the handleSubmit function to parent via callback
  // This allows the parent (ActionPlanDetails) to trigger form submission from Save button
  React.useEffect(() => {
    if (onFormSubmitReady && type === 'edit') {
      // Provide a stable wrapper that calls the latest handleSubmit via ref
      const stableSubmit = () => {
        const fn = handleSubmitRef.current;
        if (fn) {
          return fn();
        }
        return Promise.resolve(null as any);
      };
      onFormSubmitReady(stableSubmit);
    }
    // Intentionally exclude handleSubmit from deps so this effect doesn't run every render
  }, [onFormSubmitReady, type]);

  // Helper to display read-only pairs matching Figma: small label + medium content
  const ReadOnlyItem: React.FC<{ label: string; content?: string | null; multiline?: boolean }> = ({ label, content, multiline }) => (
    <View style={createActionPlanFormStyles.readOnlyItem}>
      <Text style={[createActionPlanFormStyles.viewLabelText, isDark && { color: colors.text.secondary }]}>{label}</Text>
      <Text style={[createActionPlanFormStyles.viewContentText, isDark && { color: colors.text.primary }, multiline && createActionPlanFormStyles.viewContentMultiline]}>{content ?? '--'}</Text>
    </View>
  );

  const formatDate = (d: Date | null) => {
    if (!d) return '--';
    try {
      // Format date WITHOUT timezone conversion
      // Month names for display
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const month = monthNames[d.getMonth()];
      const day = d.getDate();
      const year = d.getFullYear();
      
      return `${month} ${day}, ${year}`;
    } catch (e) {
      return String(d);
    }
  };

  // Helper to detect if selectedSource represents 'General'
  const isSourceGeneral = React.useMemo(() => {
    if (!selectedSource) return false;
    const v = (selectedSource as any).value ?? (selectedSource as any).displayText ?? '';
    return String(v).toLowerCase() === 'general';
  }, [selectedSource]);

  // Handle source change - clear type when source changes
  const handleSourceChange = (val: any) => {
    const newSource = val;
    const prevIsGeneral = selectedSource ? String((selectedSource as any).value ?? (selectedSource as any).displayText ?? '').toLowerCase() === 'general' : false;
    const newIsGeneral = newSource ? String((newSource as any).value ?? (newSource as any).displayText ?? '').toLowerCase() === 'general' : false;
    
    // Clear type when switching between General and non-General sources
    if (prevIsGeneral !== newIsGeneral) {
      setSelectedType(null);
    }
    
    setSelectedSource(newSource);
  };

  return (
    <View style={createActionPlanFormStyles.root}>
      {/* Error Banner */}
      {submissionError && (
        <View style={[createActionPlanFormStyles.errorBanner, isDark && { backgroundColor: colors.statusBackground.error, borderLeftColor: colors.status.error }]}>
          <View style={createActionPlanFormStyles.errorBannerContent}>
            <MaterialIcons name="error" size={20} color={isDark ? colors.status.error : '#D32F2F'} />
            <View style={createActionPlanFormStyles.errorTextContainer}>
              <Text style={[createActionPlanFormStyles.errorTitle, isDark && { color: colors.text.primary }]}>{submissionError.title}</Text>
              <Text style={[createActionPlanFormStyles.errorMessage, isDark && { color: colors.text.secondary }]}>{submissionError.message}</Text>
            </View>
          </View>
          <TouchableOpacity
            onPress={() => setSubmissionError(null)}
            style={createActionPlanFormStyles.errorCloseButton}
          >
            <MaterialIcons name="close" size={20} color={isDark ? colors.status.error : '#D32F2F'} />
          </TouchableOpacity>
        </View>
      )}
      
      <View style={[createActionPlanFormStyles.card, isDark && { backgroundColor: colors.background.surface, shadowColor: 'transparent' }]}>
        {/* Row 1: Title, Source */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile, isView && (!isTablet ? createActionPlanFormStyles.rowWrapViewMobile : createActionPlanFormStyles.rowWrapView)]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Title" content={title ||'--'} />
            ) : (
              <>
                <Label text="Title" required isDark={isDark} colors={colors} />
                <LhTextInput placeholder="Action Plan" value={title} onChangeText={setTitle} editable={!isView} placeholderTextColor={isDark ? colors.text.disabled : '#6c757d'} />
              </>
            )}
          </View>

          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Source" content={selectedSource?.displayText ?? '--'} />
            ) : (
              <>
                <Label text="Source" required showInfo isDark={isDark} colors={colors} />
                <CrossPlatformDropdown
                  childList={sourceOptions}
                  label={''}
                  labelStyle={{ fontWeight: "400",fontSize:12, marginTop:6, marginBottom:6 }}
                  placeholder="Select source"
                  placeholderStyle={{ fontSize:14 }}
                  selection="single"
                  setSelectedValue={handleSourceChange}
                  selectedValue={selectedSource}
                  isDisabled={isView}
                  readOnly={isView}
                  disableSorting={true}
                />
              </>
            )}
            {validationErrors?.source && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.source}</Text>}
          </View>
        </View>

        {/* Row 2: Type, Priority */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile, isView && (!isTablet ? createActionPlanFormStyles.rowWrapViewMobile : createActionPlanFormStyles.rowWrapView)]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Type" content={selectedType?.displayText ?? (selectedType ?? '--')} />
            ) : (
              <>
                <Label text="Type" required showInfo isDark={isDark} colors={colors} />
                {/* If Source is 'General' show a free-text input for Type, otherwise show the dropdown */}
                {isSourceGeneral ? (
                  <LhTextInput
                    placeholder="Enter Type"
                    value={typeof selectedType === 'string' ? selectedType : (selectedType?.displayText ?? '')}
                    onChangeText={(val) => setSelectedType(val as any)}
                    editable={!isView}
                    placeholderTextColor={isDark ? colors.text.disabled : '#6c757d'}
                  />
                ) : (
                  <CrossPlatformDropdown
                    childList={typeOptions}
                    label={''}
                    labelStyle={{ fontWeight: "400",fontSize:12 }}
                    placeholder="Action Type"
                    placeholderStyle={{ fontSize:14 }}
                    selection="single"
                    setSelectedValue={(val) => setSelectedType(val as any)}
                    selectedValue={selectedType}
                    isDisabled={isView}
                    readOnly={isView}
                    disableSorting={true}
                  />
                )}
              </>
            )}
            {validationErrors?.type && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.type}</Text>}
          </View>

          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Priority" content={selectedPriority?.displayText ?? (selectedPriority ?? '--')} />
            ) : (
              <>
                <Label text="Priority" required showInfo isDark={isDark} colors={colors} />
                <CrossPlatformDropdown
                  childList={priorityOptions}
                  label={''}
                  labelStyle={{ fontWeight: "400",fontSize:12 }}
                  placeholder="Select Priority"
                  placeholderStyle={{ fontSize:14 }}
                  selection="single"
                  setSelectedValue={(val) => setSelectedPriority(val as any)}
                  selectedValue={selectedPriority}
                  isDisabled={isView}
                  readOnly={isView}
                  disableSorting={true}
                />
              </>
            )}
            {validationErrors?.priority && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.priority}</Text>}
          </View>
        </View>

        {/* Row 3: Action Plan Owner, Assignee */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile, isView && (!isTablet ? createActionPlanFormStyles.rowWrapViewMobile : createActionPlanFormStyles.rowWrapView)]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Action Plan Owner" content={selectedOwner?.displayText ?? '--'} />
            ) : (
              <CrossPlatformDropdown
                childList={ownerOption}
                label="Action Plan Owner"
                labelStyle={{ fontWeight: '400', fontSize: 12 }}
                placeholder={'--'}
                placeholderStyle={{ fontSize: 14 }}
                selection="single"
                // Readonly dropdown: for create show the current auth user, for edit show the action plan's owner
                setSelectedValue={() => { /* noop - readonly */ }}
                selectedValue={type === 'create' ? ownerOption[0] : (selectedOwner ?? ownerOption[0])}
                isDisabled={true}
                readOnly={true}
                disabledBackgroundColor="#DADADA"
              />
            )}
          </View>

          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Assignee" content={selectedAssignee?.displayText ?? '--'} />
            ) : (
              <>
                <Label text="Assignee" required showInfo isDark={isDark} colors={colors} />
                <CrossPlatformDropdown
                  childList={assigneeList}
                  label={''}
                  labelStyle={{ fontWeight: '400', fontSize: 12 }}
                  placeholder={
                    assigneeLoading 
                      ? 'Loading...' 
                      : selectedAssignee?.displayText ?? 'Select Assignee'
                  }
                  placeholderStyle={{ fontSize: 14 }}
                  selection="single"
                  setSelectedValue={(val) => setSelectedAssignee(val as any)}
                  selectedValue={selectedAssignee}
                  isDisabled={isView}
                  readOnly={isView}
                />
              </>
            )}
            {(validationErrors?.assignee || validationErrors?.assignee_gpid || validationErrors?.assignee_email || validationErrors?.assignee_role) && (
              <Text style={createActionPlanFormStyles.errorText}>
                {validationErrors.assignee ?? validationErrors.assignee_gpid ?? validationErrors.assignee_email ?? validationErrors.assignee_role}
              </Text>
            )}
          </View>
        </View>

        {/* Row 4: Start Date, Target Date */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile, isView && (!isTablet ? createActionPlanFormStyles.rowWrapViewMobile : createActionPlanFormStyles.rowWrapView)]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Start Date" content={formatDate(startDate)} />
            ) : (
              <>
                <Label text="Start Date" required isDark={isDark} colors={colors} />
                <LhDatePicker label={''} value={startDate} onChange={setStartDate} placeholder="MM/DD/YYYY" disabled={isView} labelStyle={{fontSize:12}} colorScheme={datePickerColorScheme} iconColor={isDark ? colors.text.primary : '#3A3A3A'} />
              </>
            )}
            {validationErrors?.start_date && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.start_date}</Text>}
          </View>

          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Target Date" content={formatDate(targetDate)} />
            ) : (
              <>
                <Label text="Target Date" required isDark={isDark} colors={colors} />
                <LhDatePicker label={''} value={targetDate} onChange={handleTargetDateChange} placeholder="MM/DD/YYYY" disabled={isView} labelStyle={{fontSize:12}} colorScheme={datePickerColorScheme} iconColor={isDark ? colors.text.primary : '#3A3A3A'} />
                {dateValidationError && <Text style={createActionPlanFormStyles.errorText}>{dateValidationError}</Text>}
              </>
            )}
            {validationErrors?.target_date && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.target_date}</Text>}
          </View>
        </View>

        {isView ? (
          // stacked column for view mode (Action Plan and Notes one under another)
          <View style={createActionPlanFormStyles.columnWrap}>
            <View style={createActionPlanFormStyles.fieldColumnFull}>
              <ReadOnlyItem label="Action Plan" content={actionPlan || '--'} multiline />
            </View>

            <View style={createActionPlanFormStyles.fieldColumnFull}>
              <ReadOnlyItem label="Notes" content={notes || '--'} multiline />
            </View>
          </View>
        ) : (
          // original side-by-side layout for create/edit
          <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile]}>
            <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
              <Label text="Action Plan" required isDark={isDark} colors={colors} />
              <LhTextArea placeholder="Type here" value={actionPlan} onChangeText={setActionPlan} editable={!isView} style={isDark ? { backgroundColor: colors.background.elevated, borderColor: colors.border.default, color: colors.text.primary } : (!isView ? undefined : createActionPlanFormStyles.inputDisabled)} />
            </View>

            <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
              <Label text="Notes" isDark={isDark} colors={colors} />
              <LhTextArea placeholder="Type here" value={notes} onChangeText={setNotes} editable={type !== 'create' && !isView} style={type === 'create' ? (isDark ? [createActionPlanFormStyles.inputDisabled, { backgroundColor: colors.background.elevated, borderColor: colors.border.default, color: colors.text.disabled }] : [createActionPlanFormStyles.inputDisabled, {borderColor: '#C0C0C0'}]) : (isDark ? { backgroundColor: colors.background.elevated, borderColor: colors.border.default, color: colors.text.primary } : (!isView ? undefined : createActionPlanFormStyles.inputDisabled))} />
            </View>
          </View>
        )}

        <View style={[createActionPlanFormStyles.checkboxRow, !isTablet && createActionPlanFormStyles.checkboxRowMobile]}>
          {!isView ? (
            <TouchableOpacity
              onPress={() => !isView && setFrontlineInvolvement((v) => !v)}
              style={createActionPlanFormStyles.checkboxTouch}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: frontlineInvolvement }}
            >
              <MaterialIcons name={frontlineInvolvement ? "check-box" : "check-box-outline-blank"} size={20} color={frontlineInvolvement ? (isDark ? colors.brand.primary : "#034895") : (isDark ? colors.text.disabled : "#6B6B6B")} />
            </TouchableOpacity>
          ) : null}

          <Text style={[createActionPlanFormStyles.checkboxLabel, isDark && { color: colors.text.primary }]}>{isView ? (frontlineInvolvement ? 'Frontline Employee involvement: Yes' : 'Frontline Employee involvement: No') : 'Frontline Employee involvement'}</Text>
        </View>

        {frontlineInvolvement && (
          <View style={createActionPlanFormStyles.employeeSection}>
            {isView ? (
              <ReadOnlyItem label="Employee Name" content={data?.employee?.displayText ?? '--'} />
            ) : (
              <>
                <Label text="Employee Name" required showInfo isDark={isDark} colors={colors} />
                <CrossPlatformDropdown
                  childList={employeeList}
                  label={''}
                  labelStyle={{ fontWeight: "400",fontSize:12 }}
                  placeholder={employeeLoading ? 'Loading...' : 'Select Employee'}
                  placeholderStyle={{ fontSize:14 }}
                  selection="single"
                  setSelectedValue={(val) => setSelectedEmployee(val as any)}
                  selectedValue={selectedEmployee}
                  isDisabled={isView}
                  readOnly={isView}
                  placement="top"
                />
                {/* Inline employee validation: prefer friendly "employee" key (Employee Name), fall back to employee_gpid/employee_name from backend */}
                {(!isView && (validationErrors?.employee || validationErrors?.employee_gpid || validationErrors?.employee_name)) && (
                  <Text style={createActionPlanFormStyles.errorText}>
                    {validationErrors.employee ?? validationErrors.employee_gpid ?? validationErrors.employee_name}
                  </Text>
                )}
              </>
            )}
          </View>
        )}

        {/* View Audit Trail Button - Only for Mobile in View Mode */}
        {isMobile && isView && onViewAuditTrail && (
          <TouchableOpacity
            onPress={onViewAuditTrail}
            style={[createActionPlanFormStyles.viewAuditTrailButton, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.brand.primary }]}
            accessibilityLabel="View Audit Trail"
          >
            <Text style={[createActionPlanFormStyles.viewAuditTrailButtonText, isDark && { color: colors.brand.primary }]}>View Audit Trail</Text>
          </TouchableOpacity>
        )}

        {type === 'create' && (
          <View style={[createActionPlanFormStyles.actionsRow, !isTablet && createActionPlanFormStyles.actionsRowMobile, isTablet && createActionPlanFormStyles.actionsRowWeb, { marginTop: frontlineInvolvement ? 40 : 10 }]}>
            <TouchableOpacity
              style={[
                createActionPlanFormStyles.tertiaryButton,
                isDark && {
                  height: 44,
                  borderWidth: 2,
                  borderColor: '#0066FF',
                  backgroundColor: '#2a2a2a',
                  paddingHorizontal: 24,
                  paddingVertical: 12,
                  borderRadius: 8,
                }
              ]}
              onPress={() => {
                setSubmissionError(null);
                // Clear any global toast validation notification so it doesn't persist after navigation
                dispatch(clearToastNotification());
                onCancel?.();
              }}
            >
              <Text style={[createActionPlanFormStyles.tertiaryText, isDark && { color: '#0066FF', fontWeight: '700', fontSize: 16 }]}>Cancel</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                createActionPlanFormStyles.button,
                isCreateDisabled || loading
                  ? isDark
                    ? {
                        height: 44,
                        backgroundColor: '#404040',
                        opacity: 0.5,
                        paddingHorizontal: 28,
                        paddingVertical: 12,
                        borderRadius: 8,
                      }
                    : createActionPlanFormStyles.disabledButton
                  : isDark
                  ? {
                      height: 44,
                      backgroundColor: '#0066FF',
                      paddingHorizontal: 28,
                      paddingVertical: 12,
                      borderRadius: 8,
                      shadowColor: '#0066FF',
                      shadowOffset: { width: 0, height: 6 },
                      shadowOpacity: 0.5,
                      shadowRadius: 12,
                      elevation: 10,
                    }
                  : createActionPlanFormStyles.primaryButton,
              ]}
              disabled={isCreateDisabled || loading}
              onPress={
                isCreateDisabled || loading
                  ? undefined
                  : async () => {
                      try {
                        await handleSubmit();
                      } finally {
                        try {
                          dispatch(refetchNotifications());
                        } catch (e) {
                          console.warn('Failed to trigger notifications refetch after create:', e);
                        }
                      }
                    }
              }
            >
              <View style={createActionPlanFormStyles.primaryContent}>
                <Text
                  style={[
                    createActionPlanFormStyles.primaryText,
                    isCreateDisabled || loading
                      ? isDark
                        ? { color: '#999999', fontWeight: '700', fontSize: 16 }
                        : createActionPlanFormStyles.disabledText
                      : isDark && { color: '#FFFFFF', fontWeight: '700', fontSize: 16 },
                  ]}
                >
                  {' '}
                  {loading ? 'Submitting...' : 'Create Action Plan'}
                </Text>
              </View>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </View>
  );
};

const Label: React.FC<{ text: string; required?: boolean; showInfo?: boolean; isDark?: boolean; colors?: any }> = ({ text, required, showInfo = true, isDark, colors }) => (
  <View style={[createActionPlanFormStyles.labelRow, { justifyContent: 'space-between' }]}>
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <Text style={[createActionPlanFormStyles.labelText, isDark && { color: colors.text.primary }]}>{text}</Text>
      {showInfo ? <MaterialIcons name="info-outline" size={14} color={isDark ? colors.text.secondary : "#6B6B6B"} style={{ marginLeft: 6 }} /> : null}
    </View>
    {required ? <Text style={[createActionPlanFormStyles.required, isDark && { color: colors.text.secondary }]}>*Required</Text> : null}
  </View>
);

export default CreateActionPlanForm;


